/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";

import { useLayout } from "@/../_metronic/layout/core";
import { useCurrentAccount } from "@/hooks/useCurrentAccount";
import {
	getResumeIndicatorB3,
	ResumeIndicatorB3Data,
} from "@/services/esus/indicator/esb/b3/getResumeIndicatorB3";
import { Paginated } from "@/@types/generics/paginated";
import { useDebounce } from "@/hooks/useDebounce";
import {
	getResumeIndicatorB3ByHealthTeam,
	ResumeIndicatorB3ByHealthTeamData,
} from "@/services/esus/indicator/esb/b3/getResumeIndicatorB3ByHealthTeam";

import { IndicatorEsbB3Resume } from "./IndicatorEsbB3Resume";
import { IndicatorEsbB3HealthTeams } from "./IndicatorEsbB3HealthTeams";
import { IndicatorEsbB3Citizens } from "./IndicatorEsbB3Citizens";
import {
	ResumeCitizenProceduresIndicatorB3Data,
	getResumeCitizensProceduresIndicatorB3,
} from "@/services/esus/indicator/esb/b3/getResumeCitizensProceduresIndicatorB3";

type IndicatorEsbB3Props = {
	selectedQuarter: string;
	selectedYear: number;
	selectedMonth: number;
	selectedIne?: string;
	isPendingToUpdate: boolean;
	setIsPendingToUpdate: (isPendingToUpdate: boolean) => void;
};

export function IndicatorEsbB3({
	selectedQuarter,
	selectedYear,
	selectedMonth,
	selectedIne,
	isPendingToUpdate,
	setIsPendingToUpdate,
}: Readonly<IndicatorEsbB3Props>) {
	const { setTitle } = useLayout();
	const { uf, ibgeCode } = useCurrentAccount();
	const pageSize = 10;
	const [resumeIndicatorB3, setResumeIndicatorB3] = useState<ResumeIndicatorB3Data>(
		new ResumeIndicatorB3Data()
	);

	const [pageNumberHealthTeamsClassifications, setPageNumberHealthTeamsClassifications] =
		useState(1);
	const [searchHealthTeamsClassification, setSearchHealthTeamsClassification] = useState("");
	const debouncedSearchHealthTeamsClassifications = useDebounce(
		searchHealthTeamsClassification,
		2000
	);

	const [paginatedHealthTeamsClassifications, setPaginatedHealthTeamsClassifications] = useState(
		new Paginated<ResumeIndicatorB3ByHealthTeamData>()
	);
	const [isLoadingHealthTeamsClassifications, setIsLoadingHealthTeamsClassifications] =
		useState(false);

	const [pageNumberCitizens, setPageNumberCitizens] = useState(1);
	const [searchCitizens, setSearchCitizens] = useState("");
	const debouncedSearchCitizens = useDebounce(searchCitizens, 2000);
	const [isEvaluatedCitizens, setIsEvaluatedCitizens] = useState<boolean | undefined>(true);
	const [paginatedCitizens, setPaginatedCitizens] = useState(
		new Paginated<ResumeCitizenProceduresIndicatorB3Data>()
	);
	const [isLoadingCitizens, setIsLoadingCitizens] = useState(false);

	async function handleFilter() {
		await Promise.all([
			handleGetResumeIndicatorB3(),
			handleGetResumeIndicatorB3ByHealthTeam(),
			handleGetResumeCitizensProceduresIndicatorB3(),
		]);
		setIsPendingToUpdate(false);
	}

	async function handleGetResumeIndicatorB3() {
		setResumeIndicatorB3(
			await getResumeIndicatorB3({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
			})
		);
	}

	async function handleGetResumeIndicatorB3ByHealthTeam(pageNumber?: number) {
		setIsLoadingHealthTeamsClassifications(true);
		setPaginatedHealthTeamsClassifications(
			await getResumeIndicatorB3ByHealthTeam({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchHealthTeamsClassification,
				pageNumber: pageNumber ?? 1,
				pageSize,
			})
		);
		setIsLoadingHealthTeamsClassifications(false);
	}

	async function handleGetAllResumeIndicatorB3ByHealthTeam() {
		return (
			await getResumeIndicatorB3ByHealthTeam({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchHealthTeamsClassification,
				pageNumber: 1,
				pageSize: paginatedHealthTeamsClassifications.totalRecords,
				export: true,
			})
		).data;
	}

	async function handleGetResumeCitizensProceduresIndicatorB3(pageNumber?: number) {
		setIsLoadingCitizens(true);
		setPaginatedCitizens(
			await getResumeCitizensProceduresIndicatorB3({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchCitizens,
				isEvaluated: isEvaluatedCitizens,
				pageNumber: pageNumber ?? 1,
				pageSize,
			})
		);
		setIsLoadingCitizens(false);
	}

	async function handleGetAllResumeCitizensProceduresIndicatorB3() {
		return (
			await getResumeCitizensProceduresIndicatorB3({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchCitizens,
				isEvaluated: isEvaluatedCitizens,
				pageNumber: 1,
				pageSize: paginatedCitizens.totalRecords,
				export: true,
			})
		).data;
	}

	useEffect(() => {
		if (!isPendingToUpdate)
			handleGetResumeIndicatorB3ByHealthTeam(pageNumberHealthTeamsClassifications);
	}, [pageNumberHealthTeamsClassifications]);

	useEffect(() => {
		if (!isPendingToUpdate) {
			if (pageNumberHealthTeamsClassifications === 1) {
				handleGetResumeIndicatorB3ByHealthTeam(pageNumberHealthTeamsClassifications);
			} else {
				setPageNumberHealthTeamsClassifications(1);
			}
		}
	}, [debouncedSearchHealthTeamsClassifications]);

	useEffect(() => {
		if (!isPendingToUpdate) handleGetResumeCitizensProceduresIndicatorB3(pageNumberCitizens);
	}, [pageNumberCitizens]);

	useEffect(() => {
		if (!isPendingToUpdate) {
			if (pageNumberCitizens === 1) {
				handleGetResumeCitizensProceduresIndicatorB3(pageNumberCitizens);
			} else {
				setPageNumberCitizens(1);
			}
		}
	}, [debouncedSearchCitizens, isEvaluatedCitizens]);

	useEffect(() => {
		if (isPendingToUpdate) handleFilter();
	}, [isPendingToUpdate]);

	useEffect(() => {
		setTitle("B3 - Taxa de Exodontia");
	}, [setTitle]);

	return (
		<>
			<IndicatorEsbB3Resume resume={resumeIndicatorB3} />
			<IndicatorEsbB3HealthTeams
				paginatedHealthTeamsClassifications={paginatedHealthTeamsClassifications}
				isLoading={isLoadingHealthTeamsClassifications}
				search={searchHealthTeamsClassification}
				setSearch={setSearchHealthTeamsClassification}
				selectedQuarter={selectedQuarter}
				pageNumber={pageNumberHealthTeamsClassifications}
				setPageNumber={setPageNumberHealthTeamsClassifications}
				handleGetAllResumeIndicatorB3ByHealthTeam={
					handleGetAllResumeIndicatorB3ByHealthTeam
				}
			/>
			<IndicatorEsbB3Citizens
				paginatedCitizens={paginatedCitizens}
				isLoading={isLoadingCitizens}
				search={searchCitizens}
				setSearch={setSearchCitizens}
				isEvaluated={isEvaluatedCitizens}
				setIsEvaluated={setIsEvaluatedCitizens}
				selectedQuarter={selectedQuarter}
				selectedYear={selectedYear}
				selectedMonth={selectedMonth}
				selectedIne={selectedIne}
				pageNumber={pageNumberCitizens}
				setPageNumber={setPageNumberCitizens}
				handleGetAllResumeCitizensProceduresIndicatorB3={
					handleGetAllResumeCitizensProceduresIndicatorB3
				}
			/>
		</>
	);
}
